import { Component } from '@angular/core';
import { ProfileService } from '@core/services/profile.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateFormatPipe } from '@core/pipes/date-format.pipe';
import { Profile } from '@shared/models/profile';
import { combineLatest } from 'rxjs';
import { Country } from '@shared/models/country';
import { LocaleService } from '@core/services/locale.service';
import { ZipCityStreet } from '@shared/models/zip-city-street';
import { NxDropdownOption } from '@aposin/ng-aquila/dropdown';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@core/services/alert.service';
import moment, { Moment } from 'moment';
import { CommonService } from '@core/services/common.service';
import { AgentSource } from '@shared/models/codebooks/agent-source';
import { ModalService } from '@core/services/modal.service';
import { HttpErrorResponse } from '@angular/common/http';
@Component({
    selector: 'app-profile-edit',
    templateUrl: './profile-edit.component.html',
    styleUrls: ['./profile-edit.component.scss'],
    standalone: false
})
export class ProfileEditComponent {

    public form: FormGroup;

    public countries: Array<Country> = [];
    public identityCardTypes: Array<string> = [];
    public zipCityStreet: Array<ZipCityStreet> = [];
    public zips: NxDropdownOption[] = [];
    public cities: NxDropdownOption[] = [];
    public streets: Array<string> = [];

    public currentLang: string;

    private profile: Profile;

    constructor(
        private commonService: CommonService,
        private profileService: ProfileService,
        private dateFormat: DateFormatPipe,
        private localeService: LocaleService,
        private router: Router,
        private translateService: TranslateService,
        private alertService: AlertService,
        private modalService: ModalService,
    ) {
        this.commonService.setToStorage('mobileVerified', false, 0);
        combineLatest({
            'identityCardTypes': this.profileService.getIdentityCardTypes(),
            'countries': this.profileService.getCountries(),
            'profile': this.profileService.getAccount(),
            'zips': this.profileService.getZips(),
            'currentLang': this.localeService.activeLanguage
        }).subscribe((result) => {

            this.profile = result.profile;
            if (this.profile.changeRequestOpened) {
                this.router.navigate(['moj-profil']);
                return;
            }
            
            this.translateService.get(
                'profile.texts.alert.overdue-id'
            ).subscribe((trans) => {
                let alertMsgs = [];

                if (!this.profile.identityCard.validTo || moment(this.profile.identityCard.validTo).isBefore(moment.now())) {
                    alertMsgs.push(trans['profile.texts.alert.overdue-id']);
                }

                this.alertService.warn(alertMsgs.join('<br>'));
            });

            this.countries = result.countries as Array<Country>;
            this.identityCardTypes = result.identityCardTypes as Array<string>;
            this.currentLang = result.currentLang.code;

            (result.zips as Array<string>).forEach(zip => {
                this.zips.push({
                    value: zip,
                });
            });

            this.profileService.getCitiesByZip(this.profile.address.zip).subscribe((cities) => {
                cities.forEach(city => {
                    this.cities.push({
                        value: city,
                    });
                });

                this.profileService.getStreetsByZipCity(this.profile.address.zip, this.profile.address.city)
                    .subscribe((streets) => {
                        streets.forEach(street => {
                            this.streets.push(street);
                        });

                        this.initForm();
                    });
            });

        });
    }

    private initForm() {

        const disabledField = this.commonService.getLoggedUser().agentSource == AgentSource.LEVEL1;

        let streetNumber = '';
        if (this.profile.address.houseNumber && this.profile.address.houseOrientationNumber) {
            streetNumber = [
                this.profile.address.houseNumber,
                this.profile.address.houseOrientationNumber,
            ].join('/');
        }

        if (!this.profile.address.houseNumber && this.profile.address.houseOrientationNumber) {
            streetNumber = this.profile.address.houseOrientationNumber;
        }

        if (this.profile.address.houseNumber && !this.profile.address.houseOrientationNumber) {
            streetNumber = this.profile.address.houseNumber;
        }

        this.form = new FormGroup({
            firstName: new FormControl({
                value: this.profile.firstName,
                disabled: disabledField
            }, Validators.required),
            lastName: new FormControl({
                value: this.profile.lastName,
                disabled: disabledField
            }, Validators.required),
            title: new FormControl({
                value: this.profile.title,
                disabled: true,
            }),
            dateOfBirth: new FormControl({
                value: this.dateFormat.transform(this.profile.dateOfBirth),
                disabled: true,
            }),
            maidenName: new FormControl({
                value: this.profile.maidenName,
                disabled: disabledField
            }),
            idNumber: new FormControl({
                value: this.profile.idNumber,
                disabled: true,
            }),
            email: new FormControl({
                value: this.profile.email,
                disabled: disabledField
            }, [Validators.required, Validators.email]),
            mobile: new FormControl({
                value: this.profile.mobile,
                disabled: disabledField
            }, [Validators.required, Validators.pattern(/^\+[1-9][0-9]{7,14}$/)]),
            idCardType: new FormControl({
                value: this.profile.identityCard.type,
                disabled: disabledField
            }),
            idCard: new FormControl({
                value: this.profile.identityCard.value,
                disabled: disabledField
            }),
            validTo: new FormControl({
                value: this.profile.identityCard.validTo,
                disabled: disabledField
            }),
            issuedBy: new FormControl({
                value: this.profile.identityCard.issuedBy,
                disabled: disabledField
            }),
            country: new FormControl({
                value: this.profile.address.country.twoLetterCountryCode,
                disabled: disabledField
            }, Validators.required),
            zip: new FormControl({
                value: this.profile.address.zip,
                disabled: disabledField
            }, Validators.required),
            city: new FormControl({
                value: this.profile.address.city,
                disabled: disabledField,
            }, Validators.required),
            street: new FormControl({
                value: this.profile.address.street,
                disabled: disabledField,
            }, Validators.required),
            houseNumber: new FormControl({
                value: streetNumber,
                disabled: disabledField
            }, [Validators.required, Validators.pattern(/([0-9]*\/[0-9a-zA-Z]*)|([0-9a-zA-Z]*)/)]),
        });

        this.form.controls.idCardType.valueChanges.subscribe( idCardType => {
            if(idCardType){
                this.form.controls.idCard.addValidators(Validators.required)
                this.form.controls.idCard.updateValueAndValidity()
            }
        })

        this.form.controls.zip.valueChanges.subscribe(zipChange => {
            this.profileService.getCitiesByZip(zipChange).subscribe((cities) => {
                this.cities = [];
                cities.forEach(city => {
                    this.cities.push({
                        value: city,
                    });
                });
                this.form.controls.city.setValue(cities.shift());
            });
        });

        this.form.controls.city.valueChanges.subscribe(cityChange => {
            this.profileService.getStreetsByZipCity(this.form.controls.zip.value, cityChange)
                .subscribe((streets) => {
                    this.streets = [];
                    streets.forEach(street => {
                        this.streets.push(street);
                    });
                    
                    if(streets.find( s => s === this.profile.address.street)){
                        this.streets.push(this.profile.address.street)
                    }

                    this.form.controls.street.setValue(streets.shift());
                });
        });
    }

    updateProfile(event: SubmitEvent) {
        this.alertService.clear();
        
        if (this.form.invalid) {
            this.translateService.get([
                'profile.edit.error.email-empty',
                'profile.edit.error.email-format',
                'profile.edit.error.mobile-empty',
                'profile.edit.error.mobile-format',
                'profile.edit.error.id-card-type-empty',
                'profile.edit.error.id-card-empty',
            ]).subscribe((trans) => {
                let alertMsgs = [];
                if (this.form.controls.email?.errors?.required) {
                    alertMsgs.push(trans['profile.edit.error.email-empty']);
                }

                if (this.form.controls.email?.errors?.pattern) {
                    alertMsgs.push(trans['profile.edit.error.email-format']);
                }

                if (this.form.controls.mobile?.errors?.required) {
                    alertMsgs.push(trans['profile.edit.error.mobile-empty']);
                }

                if (this.form.controls.mobile?.errors?.pattern) {
                    alertMsgs.push(trans['profile.edit.error.mobile-format']);
                }

                if (this.form.controls.idCardType?.errors?.required) {
                    alertMsgs.push(trans['profile.edit.error.id-card-type-empty']);
                }

                if (this.form.controls.idCard?.errors?.required) {
                    alertMsgs.push(trans['profile.edit.error.id-card-empty']);
                }

                this.alertService.error(alertMsgs.join('<br>'));
            });

            return;
        }

        const mobileVerified = this.commonService.getFromStorage('mobileVerified');
        if (this.profile.mobile !== this.form.controls.mobile.value && !mobileVerified) {
            this.profileService.generateSMSCode(this.profile.mobile, 'update_profile_mobile').subscribe(
                {
                    error: (errorResponse: HttpErrorResponse) => {
                        if(errorResponse.error['msg'] == 'attemptsExceeded'){
                            this.translateService.get('error.sms.wait').subscribe( (trans) => {
                                this.alertService.error(trans);
                            })
                        }
                    },
                    next: (response) => {
                        const smsCode = response['pin'] ? response['pin'] : null
                        this.modalService.openModal('UpdateMobileCheckPIN', {
                            mobile: this.profile.mobile,
                            smsCode: smsCode
                        }).confirm.subscribe((submitted) => {
                            if(!submitted){
                                return;
                            }

                            this.commonService.setToStorage('mobileVerified', true, 1440);
                            let formValues = this.form.value;
                            this.profileService.postAccountUpdate('editProfile', this.form.value).subscribe(() => {
                                this.router.navigate(['moj-profil']);
                                return;
                            });
                        });
                    }
                });

            return;
        }

        this.profileService.postAccountUpdate('editProfile', this.form.value).subscribe({
            error: (errorResponse: HttpErrorResponse) => {
                this.translateService.get('profile.edit.error.fail').subscribe((trans) => {
                    this.alertService.error(trans);
                });
            },
            next: () => {
                this.router.navigate(['moj-profil']);
                return;
            }
        });
    }
}
